// @react-compiler enable
import {SingleSelectToken} from '@github-ui/shared-single-select-components/SingleSelectToken'
import {testIdProps} from '@github-ui/test-id-props'
import {clsx} from 'clsx'
import {memo} from 'react'

import type {MemexColumnDataType} from '../../../api/columns/contracts/memex-column'
import type {MultiSelectValue} from '../../../api/columns/contracts/multi-select'
import type {PersistedOption} from '../../../api/columns/contracts/single-select'
import {TableRowHeight} from '../../../api/view/contracts'
import {useTableRowHeight} from '../../../hooks/use-table-row-height'
import type {ColumnModelForDataType} from '../../../models/column-model'
import {type ColumnValue, hasValue, isLoading} from '../../../models/column-value'
import type {MemexItemModel} from '../../../models/memex-item-model'
import {PillPlaceholder} from '../../common/placeholders'
import {BaseCell} from '../cells/base-cell'
import {DropdownCell} from '../cells/dropdown-cell'
import {useRecordCellRenderer} from '../performance-measurements'
import styles from './multi-select-renderer.module.css'

type Props = Readonly<{
  currentValue: ColumnValue<MultiSelectValue>
  model: MemexItemModel
  options: ColumnModelForDataType<typeof MemexColumnDataType.MultiSelect>['settings']['options']
  dropdownRef?: React.RefObject<HTMLButtonElement | null>
  isDisabled?: boolean
}>

export const LoadingMultiSelectCell = () => (
  <BaseCell>
    <PillPlaceholder minWidth={40} maxWidth={80} {...testIdProps('placeholder')} />
  </BaseCell>
)

/**
 * Resolves the selected option IDs against the column's option settings,
 * preserving the order in which the options were configured. Unknown IDs are
 * dropped (the option may have been deleted after the value was saved).
 */
function selectedOptions(value: MultiSelectValue, options: ReadonlyArray<PersistedOption>): Array<PersistedOption> {
  if (value.options.length === 0 || options.length === 0) return []

  const selectedIds = new Set(value.options.map(o => o.id))
  return options.filter(option => selectedIds.has(option.id))
}

export const MultiSelectRenderer = memo(function MultiSelectRendererComponent({
  currentValue,
  model,
  dropdownRef,
  options,
  isDisabled,
}: Props) {
  useRecordCellRenderer('MultiSelectRenderer', model.id)
  const {rowHeight} = useTableRowHeight()
  const isWrapping = rowHeight !== TableRowHeight.Single

  if (isLoading(currentValue)) {
    return <LoadingMultiSelectCell />
  }

  const matchedOptions = hasValue(currentValue) ? selectedOptions(currentValue.value, options) : []

  if (matchedOptions.length === 0) {
    return <DropdownCell ref={dropdownRef} isDisabled={isDisabled} />
  }

  return (
    <DropdownCell ref={dropdownRef} isDisabled={isDisabled}>
      <div
        className={clsx(styles.TokenList, isWrapping && styles.wrapping)}
        {...testIdProps('multi-select-token-list')}
      >
        {matchedOptions.map(option => (
          <SingleSelectToken key={option.id} option={option} />
        ))}
      </div>
    </DropdownCell>
  )
})
